//#region Imports
import React, { useState, useEffect, Suspense } from "react";
import _ from "lodash";
import Bowser from "bowser";

import { Route, Navigate, useLocation, Outlet } from "react-router";

// Grafana
import { faro, FaroRoutes, LogLevel } from "@grafana/faro-react";

import { ProtectedRoute } from "./additional/components/Protected Route";

import { Box, styled } from "@mui/material";

import useMediaQuery from "@mui/material/useMediaQuery";

import { SnackbarProvider, MaterialDesignContent } from "notistack";

import { useTheme } from "@mui/material/styles";

import {
  useGetAuthStatusQuery,
  usePostRefreshMutation,
} from "./store/api/auth api";
import { useDispatch, useSelector } from "react-redux";
import { initUser, setIsLogin } from "./store/slicers/User Info Slicer";
import { getCookie } from "./additional/functions";
import { collectWebVitals } from "./additional/metric collector";

// Import Page Elements
import Header from "./page elements/Header/Header";
import Footer from "./page elements/Footer";
import ScrollUpButton from "./page elements/Scroll Up Button";
import AdminPanelWrapper from "./additional/components/page specific/Admin Panel Wrapper";
import ThemeChangeModal from "./page elements/Theme Change Modal";
import LoadingPage from "./page elements/Loading Page";

// Import Pages
const Login = React.lazy(() => import("./pages/Login"));
const GoogleAuthenticator = React.lazy(() =>
  import("./pages/Google Authenticator")
);
const AdminPanel = React.lazy(() => import("./pages/Admin Panel"));
const AdminPanelUserPage = React.lazy(() =>
  import("./pages/Admin Panel - User Page")
);
const Manuals = React.lazy(() => import("./pages/Manuals"));
const Scripts = React.lazy(() => import("./pages/Scripts"));
const AIModels = React.lazy(() => import("./pages/AI Models"));
const UiRemote = React.lazy(() => import("./pages/Ui Remote"));
const DRSLists = React.lazy(() => import("./pages/DRS Lists Table"));
const MappingAlias = React.lazy(() => import("./pages/Mapping Alias"));
const NotExistPage = React.lazy(() => import("./pages/NotExistPage"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const APIKeys = React.lazy(() => import("./pages/API Keys"));

import {
  setLocalStorageValue,
  getLocalStorageValue,
} from "./additional/functions";

import { BASE_PATHS } from "../constants/pathes";
import { PERMISSION_NAMES } from "../constants/permissions";

//#endregion

const App = () => {
  //#region Hooks
  const isDesktop = useMediaQuery("(min-width:1046px)");
  const theme = useTheme();
  const isLogin = useSelector((state) => state.userInfo.isLogin || false);
  const expireTime = useSelector((state) => state.userInfo.expireTime || 0);
  const { data, refetch, isLoading, error } = useGetAuthStatusQuery();
  const [refresh] = usePostRefreshMutation();
  const dispatch = useDispatch();
  const location = useLocation();
  //#endregion

  //#region States
  const [xPaddings, setXPaddings] = useState(0);
  const [lastKnownLocation, setLastKnownLocation] = useState(
    BASE_PATHS.googleAuthenticator
  );

  const getMainStyles = () => {
    const baseMainStyles = {
      px: xPaddings,
      flex: "1",
      minHeight: "84vh",
    };

    let mainStyles;

    switch (location.pathname) {
      case BASE_PATHS.uiRemote: {
        mainStyles = {
          py: 0,
          display: "flex",
          flexDirection: "column",
          paddingBottom: 5,
        };
        break;
      }
      case BASE_PATHS.aiModels: {
        mainStyles = {
          display: "flex",
          py: 6.25,
          flexDirection: "column",
          rowGap: 4,
        };
        break;
      }
      default: {
        mainStyles = {
          py: 6.25,
        };
        break;
      }
    }

    return _.merge({}, baseMainStyles, mainStyles);
  };
  //#endregion

  //#region Effects
  useEffect(() => {
    const interval = setInterval(async () => {
      const refreshToken = getCookie("csrf_refresh_token");
      const accessToken = getCookie("csrf_access_token");

      if (!refreshToken && !accessToken) {
        dispatch(setIsLogin(false));
        faro.api.resetUser();
      } else if (!!refreshToken && !accessToken) {
        try {
          await refresh().unwrap();
        } catch (error) {
          console.error(error);
        }
        refetch();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    isDesktop ? setXPaddings(9.375) : setXPaddings(4.6875);
  }, [isDesktop]);

  useEffect(() => {
    if (isLoading) return;

    if (error?.status === 401) {
      dispatch(setIsLogin(false));
      return;
    }
    if (data) {
      dispatch(
        initUser({
          email: data.email,
          firstName: data.first_name,
          fullName: data.full_name,
          isLogin: true,
          pages: data.pages,
          expireTime: data.exp,
          permissions: data.permissions,
        })
      );
      faro.api.setUser({
        email: data.email,
      });
    } else {
      dispatch(setIsLogin(false));
      faro.api.resetUser();
    }
  }, [data, isLoading, error]);

  // Set refetch the user auth status on token expire time
  useEffect(() => {
    const currentTime = Math.floor(Date.now() / 1000); // Current time (seconds)
    const delay = (expireTime - currentTime) * 1000; // Time to refetch (ms)

    if (delay > 0) {
      const timer = setTimeout(async () => {
        try {
          await refresh().unwrap();
        } catch (error) {
          console.error(error);
        }
        refetch();
      }, delay);

      return () => clearTimeout(timer); // Clear timer
    }
  }, [refetch, expireTime]);

  // Update last known location
  useEffect(() => {
    collectWebVitals(location.pathname);
    location.pathname != BASE_PATHS.login &&
      location.pathname != BASE_PATHS.notExistingPage &&
      setLocalStorageValue(
        "lastKnownLocation",
        `${location.pathname}${location.search}`
      );
  }, [location]);

  // Get last known location on page mounting
  useEffect(() => {
    const localStorageInfo = getLocalStorageValue("lastKnownLocation");
    setLastKnownLocation(
      localStorageInfo === "/"
        ? BASE_PATHS.googleAuthenticator
        : localStorageInfo
    );
  }, []);

  useEffect(() => {
    const browser = Bowser.getParser(window.navigator.userAgent);

    faro.api.pushLog(["User Agent Information"], {
      context: {
        user_agent_name: browser.getBrowserName(),
        user_agent_version: browser.getBrowserVersion(),
        user_platform_type: browser.getPlatformType(),
      },
      level: LogLevel.INFO,
      skipDedupe: false,
    });
  }, []);
  //#endregion

  // Shackbars styling
  const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
    "&.notistack-MuiContent-success": {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
    "&.notistack-MuiContent-error": {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    },
    "&.notistack-MuiContent-warning": {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
    "&.notistack-MuiContent-info": {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.info.contrastText,
    },
  }));

  return (
    <SnackbarProvider
      maxSnack={3}
      autoHideDuration={3000}
      preventDuplicate
      Components={{
        success: StyledMaterialDesignContent,
        error: StyledMaterialDesignContent,
        warning: StyledMaterialDesignContent,
        info: StyledMaterialDesignContent,
      }}>
      <FaroRoutes>
        <Route
          path="/"
          element={
            isLogin ? (
              <Navigate to={lastKnownLocation} replace />
            ) : (
              <Navigate to={BASE_PATHS.login} replace />
            )
          }
        />
        <Route
          path={BASE_PATHS.login}
          element={
            isLogin ? <Navigate to={lastKnownLocation} replace /> : <Login />
          }
        />
        <Route
          element={
            <>
              <ScrollUpButton />
              <ThemeChangeModal />
              <Header isDesktop={isDesktop} xPaddings={xPaddings} />
              <Box component="main" sx={getMainStyles()}>
                <Suspense fallback={<LoadingPage />}>
                  <Outlet />
                </Suspense>
              </Box>
              <Footer />
            </>
          }>
          <Route
            path={BASE_PATHS.googleAuthenticator}
            element={
              <ProtectedRoute>
                <GoogleAuthenticator />
              </ProtectedRoute>
            }
          />
          <Route
            path={BASE_PATHS.scripts}
            element={
              <ProtectedRoute>
                <Scripts />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${BASE_PATHS.apiKeys}/*`}
            element={
              <ProtectedRoute>
                <APIKeys />
              </ProtectedRoute>
            }
          />
          <Route
            path={BASE_PATHS.adminPanel}
            element={
              <ProtectedRoute>
                <AdminPanelWrapper />
              </ProtectedRoute>
            }>
            <Route index element={<AdminPanel />} />
            <Route path="user/:email" element={<AdminPanelUserPage />} />
          </Route>
          <Route
            path={BASE_PATHS.manuals}
            element={
              <ProtectedRoute>
                <Manuals />
              </ProtectedRoute>
            }
          />
          <Route
            path={BASE_PATHS.aiModels}
            element={
              <ProtectedRoute permissionId={PERMISSION_NAMES.aiModels}>
                <AIModels />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${BASE_PATHS.mappingAlias}/*`}
            element={
              <ProtectedRoute permissionId={PERMISSION_NAMES.aliases}>
                <MappingAlias />
              </ProtectedRoute>
            }
          />
          <Route
            path={BASE_PATHS.uiRemote}
            element={
              <ProtectedRoute permissionId={PERMISSION_NAMES.uiremoteConcat}>
                <UiRemote />
              </ProtectedRoute>
            }
          />
          <Route
            path={BASE_PATHS.drsPipe}
            element={
              <ProtectedRoute permissionId={PERMISSION_NAMES.drsPipe}>
                <DRSLists />
              </ProtectedRoute>
            }
          />
          <Route
            path={BASE_PATHS.dashboard}
            element={
              <ProtectedRoute permissionId={PERMISSION_NAMES.dashboard}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
        </Route>
        {/* Redirects to 404 page */}
        <Route path={BASE_PATHS.notExistingPage} element={<NotExistPage />} />
        <Route
          path="*"
          element={<Navigate to={BASE_PATHS.notExistingPage} replace />}
        />
      </FaroRoutes>
    </SnackbarProvider>
  );
};

export default App;
